import {
  StandardRecipeV1,
  NutritionInfo,
  ScalerIngredient,
} from "../types/StandardRecipe";

import { normalizeUnit } from "../shared/culinary";

import { normalizeIngredientLine } from "../shared/ingredientFormat";

type AnyObj = Record<string, any>;

function parseAmount(value: any): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && !Number.isNaN(value)) return value;

  const s = String(value).trim();
  if (!s) return undefined;

  // "1 1/2" → 1.5 ;  "1/2" → 0.5 ; "½" → 0.5 ; "0.25" → 0.25
  // split mixed number "a b/c"
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const a = Number(mixed[1]);
    const b = Number(mixed[2]);
    const c = Number(mixed[3]) || 1;
    return a + b / c;
  }

  // simple fraction "b/c"
  const frac = /^(\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const b = Number(frac[1]);
    const c = Number(frac[2]) || 1;
    return b / c;
  }

  // unicode half/quarter/three-quarter quick pass
  const unicodeMap: Record<string, number> = { "½": 0.5, "¼": 0.25, "¾": 0.75 };
  if (s in unicodeMap) return unicodeMap[s];

  const n = Number(s);
  return Number.isNaN(n) ? undefined : n;
}

function canonicalizeName(name: string): string {
  // light normalization (keep it deterministic; avoid heavy NLP here)
  // capitalise common words like "Greek" if you have a whitelist, or just trim:
  return name.trim().replace(/\s+/g, " ");
}

function coerceString(v: any, fallback = ""): string {
  return typeof v === "string" && v.trim().length ? v : fallback;
}

function coerceArray<T = string>(v: any): T[] {
  if (Array.isArray(v)) return v as T[];
  if (typeof v === "string")
    return v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean) as T[];
  return [];
}

function ensureISO(d?: any): string {
  try {
    return d ? new Date(d).toISOString() : new Date().toISOString();
  } catch {
    return new Date().toISOString();
  }
}

function pickImageUrl(src: AnyObj): string {
  const candidates = [
    src.imageUrl,
    src.heroImageUrl,
    src.image,
    src.thumbnail,
    src.photoUrl,
  ].filter(Boolean);
  return coerceString(
    candidates[0],
    `https://picsum.photos/seed/${src.id ?? "recipe"}/800/600.jpg`,
  );
}

function normalizeNutrition(src: AnyObj): NutritionInfo | undefined {
  const calories = Number(src.calories ?? src.nutrition?.calories);
  const proteinGrams = Number(src.proteinGrams ?? src.nutrition?.proteinGrams);
  const carbsGrams = Number(src.carbsGrams ?? src.nutrition?.carbsGrams);
  const fatGrams = Number(src.fatGrams ?? src.nutrition?.fatGrams);
  const anyPresent = [calories, proteinGrams, carbsGrams, fatGrams].some(
    (v) => !Number.isNaN(v),
  );
  return anyPresent
    ? { calories, proteinGrams, carbsGrams, fatGrams }
    : undefined;
}

function normalizeIngredients(src: AnyObj): ScalerIngredient[] | undefined {
  const arr = src.ingredients ?? src.ingredientList;
  const out = coerceArray<AnyObj>(arr)
    .map((i) => {
      // 1) Free-text line → normalize once, store in `name` if we can't split it reliably
      if (typeof i === "string") {
        const normalizedLine = normalizeIngredientLine(i);
        // If you later add a parser to split amount/unit/name from the normalizedLine,
        // you can populate amount/unit here too. For now, keep it safe & readable.
        return {
          name: normalizedLine,
          // amount: undefined,
          // unit: "",
        } as ScalerIngredient;
      }

      // 2) Structured object → normalize fields
      const rawName =
        coerceString(i.name) ||
        coerceString(i.ingredient?.name) ||
        coerceString(i.notes) ||
        coerceString(i.ingredientId) ||
        coerceString(i.originalName);

      const rawAmount =
        i.amount ?? i.quantity ?? i.qty ?? i.q ?? i.count ?? i.n;

      const rawUnit = i.unit ?? i.units ?? i.u;

      const name = canonicalizeName(rawName);
      const amount = parseAmount(rawAmount);
      const unit = normalizeUnit(coerceString(rawUnit));
      const note = coerceString(i.note || i.notes) || undefined;

      // If we somehow still have nothing meaningful, fall back to free-text normalization
      if (!name && i.originalString) {
        const normalizedLine = normalizeIngredientLine(
          coerceString(i.originalString),
        );
        return { name: normalizedLine } as ScalerIngredient;
      }

      return {
        name,
        amount,
        unit,
        note,
        // Keep a raw field for debugging/analytics if you want
        raw: coerceString(i.raw) || coerceString(i.originalString) || undefined,
      } as ScalerIngredient;
    })
    .filter((i) => Boolean(i && i.name && i.name.trim().length));

  return out.length ? out : undefined;
}

function baseShape(
  src: AnyObj,
  origin: string,
): Omit<
  StandardRecipeV1,
  | "id"
  | "title"
  | "description"
  | "imageUrl"
  | "slug"
  | "origin"
  | "createdAt"
  | "updatedAt"
  | "_version"
> {
  return {
    totalMinutes:
      Number(src.totalMinutes ?? src.time ?? src.readyInMinutes) || undefined,
    prepTime: Number(src.prepTime) || undefined,
    cookTime: Number(src.cookTime) || undefined,
    servings: Number(src.servings ?? src.yield) || undefined,
    difficulty: ["easy", "medium", "hard"].includes(src.difficulty)
      ? src.difficulty
      : undefined,
    cuisine: coerceString(src.cuisine) || undefined,
    tags: coerceArray(src.tags),
    nutrition: normalizeNutrition(src),
    ingredients: normalizeIngredients(src),
    instructions: coerceArray<string>(src.instructions),
    score: typeof src.score === "number" ? src.score : undefined,
    matchingFactors: coerceArray<string>(src.matchingFactors),
    personalizedReasons: coerceArray<string>(src.personalizedReasons),
    likedAt: origin === "favorite" ? ensureISO(src.likedAt) : undefined,
  };
}

function finalize(recipe: any): StandardRecipeV1 {
  return {
    ...recipe,
    title: recipe.title.trim(),
    description: recipe.description.trim(),
    imageUrl: recipe.imageUrl.trim(),
    tags: recipe.tags ?? [],
    _version: recipe._version ?? 1,
  };
}

// Hash utility for stdHash
function hashString(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

// Minimal random id without importing extra deps
function cryptoRandomId(): string {
  const bytes =
    typeof crypto !== "undefined" && "getRandomValues" in crypto
      ? crypto.getRandomValues(new Uint8Array(8))
      : Array.from({ length: 8 }, () => Math.floor(Math.random() * 256));
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export class RecipeDataMapper {
  static favorite(source: AnyObj): StandardRecipeV1 {
    const id = coerceString(source.id) || cryptoRandomId();
    const imageUrl = pickImageUrl(source);
    return finalize({
      id,
      origin: "favorite",
      title: coerceString(source.title, "Untitled Recipe"),
      description: coerceString(source.description, "A saved favorite recipe."),
      imageUrl,
      slug: coerceString(source.slug, id),
      createdAt: ensureISO(source.createdAt),
      updatedAt: ensureISO(source.updatedAt ?? source.createdAt),
      ...baseShape(source, "favorite"),
      _version: 1,
    });
  }

  static custom(source: AnyObj): StandardRecipeV1 {
    const id = coerceString(source.id) || cryptoRandomId();
    const imageUrl = pickImageUrl(source);
    return finalize({
      id,
      origin: "custom",
      title: coerceString(source.title, "Untitled Custom Recipe"),
      description: coerceString(source.description, ""),
      imageUrl,
      slug: coerceString(source.slug, id),
      createdAt: ensureISO(source.createdAt),
      updatedAt: ensureISO(source.updatedAt ?? source.createdAt),
      ...baseShape(source, "custom"),
      _version: 1,
    });
  }

  static personalized(source: AnyObj): StandardRecipeV1 {
    const id = coerceString(source.id) || cryptoRandomId();
    const imageUrl = pickImageUrl(source);
    return finalize({
      id,
      origin: "personalized",
      title: coerceString(source.title, "Recommended Recipe"),
      description: coerceString(source.description, "Recommended for you."),
      imageUrl,
      slug: coerceString(source.slug, id),
      createdAt: ensureISO(source.createdAt),
      updatedAt: ensureISO(source.updatedAt ?? source.createdAt),
      ...baseShape(source, "personalized"),
      _version: 1,
    });
  }

  static toStandardRecipe(source: AnyObj, origin: string): StandardRecipeV1 {
    switch (origin) {
      case "favorite":
        return this.favorite(source);
      case "custom":
        return this.custom(source);
      case "personalized":
        return this.personalized(source);
      default: {
        const id = coerceString(source.id) || cryptoRandomId();
        const imageUrl = pickImageUrl(source);
        return finalize({
          id,
          origin: "external",
          title: coerceString(source.title, "Recipe"),
          description: coerceString(source.description, ""),
          imageUrl,
          slug: coerceString(source.slug, id),
          createdAt: ensureISO(source.createdAt),
          updatedAt: ensureISO(source.updatedAt ?? source.createdAt),
          ...baseShape(source, "external"),
          _version: 1,
        });
      }
    }
  }

  static createSnapshot(
    recipe: StandardRecipeV1,
    userId: string,
    sourceId?: string,
  ): {
    userId: string;
    origin: string;
    sourceId?: string;
    title: string;
    cuisine?: string;
    tags: string[];
    std: StandardRecipeV1;
    version: number;
    stdHash: string;
  } {
    const stdJson = JSON.stringify(recipe);
    return {
      userId,
      origin: recipe.origin,
      sourceId,
      title: recipe.title,
      cuisine: recipe.cuisine,
      tags: recipe.tags,
      std: recipe,
      version: 1,
      stdHash: hashString(stdJson),
    };
  }
}

export default RecipeDataMapper;